import * as tslib_1 from "tslib";
import hoistNonReactStatics from 'hoist-non-react-statics';
import memoize from 'lodash/memoize';
import React, { Component } from 'react';
var DEFAULT_OPTIONS = {
  composeTheme: 'deeply',
  mapThemrProps: defaultMapThemrProps
};
var THEMR_CONFIG = Symbol('THEMR_CONFIG');
export var themr = function themr(componentName, localTheme, options) {
  if (options === void 0) {
    options = {};
  }

  return function (ThemedComponent) {
    var _a = tslib_1.__assign({}, DEFAULT_OPTIONS, options),
        optionComposeTheme = _a.composeTheme,
        optionMapThemrProps = _a.mapThemrProps;

    var componentConfig = ThemedComponent[THEMR_CONFIG];

    if (componentConfig && componentConfig.componentName === componentName) {
      componentConfig.localTheme = themeable(componentConfig.localTheme, localTheme);
      return ThemedComponent;
    }

    var config = {
      componentName: componentName,
      localTheme: localTheme
    };

    var getTheme = function getTheme(_a, contextTheme) {
      var theme = _a.theme,
          composeTheme = _a.composeTheme,
          themeNamespace = _a.themeNamespace;
      return composeTheme === 'softly' ? tslib_1.__assign({}, contextTheme, config.localTheme, getNamespacedTheme({
        themeNamespace: themeNamespace,
        theme: theme
      })) : themeable(themeable(contextTheme, config.localTheme), getNamespacedTheme({
        themeNamespace: themeNamespace,
        theme: theme
      }));
    };

    var getNamespacedTheme = function getNamespacedTheme(_a) {
      var theme = _a.theme,
          themeNamespace = _a.themeNamespace;

      if (!themeNamespace) {
        return theme;
      }

      if (themeNamespace && !theme) {
        throw new Error('Invalid themeNamespace use in react-css-themr. ' + 'themeNamespace prop should be used only with theme prop.');
      }

      return Object.keys(theme).filter(function (key) {
        return key.startsWith(themeNamespace);
      }).reduce(function (result, key) {
        var _a;

        return tslib_1.__assign({}, result, (_a = {}, _a[removeNamespace(key, themeNamespace)] = theme[key], _a));
      }, {});
    };

    var getThemeNotComposed = function getThemeNotComposed(_a, contextTheme) {
      var theme = _a.theme,
          themeNamespace = _a.themeNamespace;

      if (theme) {
        return getNamespacedTheme({
          themeNamespace: themeNamespace,
          theme: theme
        });
      }

      if (config.localTheme) {
        return config.localTheme;
      }

      return contextTheme;
    };

    var calcTheme = memoize(function (theme, composeTheme, themeNamespace, contextTheme) {
      return composeTheme ? getTheme({
        theme: theme,
        composeTheme: composeTheme,
        themeNamespace: themeNamespace
      }, contextTheme) : getThemeNotComposed({
        theme: theme,
        themeNamespace: themeNamespace
      }, contextTheme);
    });

    var Themed =
    /** @class */
    function (_super) {
      tslib_1.__extends(Themed, _super);

      function Themed(props, ctx) {
        return _super.call(this, props, ctx) || this;
      }

      Themed.prototype.getContextTheme = function () {
        return this.context.themr ? this.context.themr.theme[config.componentName] : {};
      };

      Themed.prototype.render = function () {
        var _a = this.props,
            theme = _a.theme,
            composeTheme = _a.composeTheme,
            themeNamespace = _a.themeNamespace;
        var calculatedTheme = calcTheme(theme, composeTheme, themeNamespace, this.getContextTheme());
        return React.createElement(ThemedComponent, this.props.mapThemrProps(this.props, calculatedTheme));
      };

      Themed.displayName = "Themed" + ThemedComponent.name;
      Themed.defaultProps = tslib_1.__assign({}, ThemedComponent.defaultProps, {
        composeTheme: optionComposeTheme,
        mapThemrProps: optionMapThemrProps
      });
      return Themed;
    }(Component);

    Themed[THEMR_CONFIG] = config;
    return hoistNonReactStatics(Themed, ThemedComponent);
  };
};
export function themeable() {
  var themes = [];

  for (var _i = 0; _i < arguments.length; _i++) {
    themes[_i] = arguments[_i];
  }

  return themes.reduce(function (acc, theme) {
    return merge(acc, theme);
  }, {});
}
var uniqueSymbol = Symbol('someuniquekey');

function isProxy(obj) {
  return obj[uniqueSymbol] === uniqueSymbol;
}

function merge(original, mixin) {
  //make a copy to avoid mutations of nested objects
  if (original === void 0) {
    original = {};
  }

  if (mixin === void 0) {
    mixin = {};
  } //in jest we're using module mappper for css


  if (process.env.NODE_ENV !== 'production') {
    if (isProxy(original)) {
      return original;
    }

    if (isProxy(mixin)) {
      return mixin;
    }
  }

  var result = tslib_1.__assign({}, original); //traverse mixin keys and merge them to resulting theme


  Object.keys(mixin).forEach(function (key) {
    var originalValue = original[key];
    var mixinValue = mixin[key];

    if (typeof originalValue === 'function' || typeof mixinValue === 'function') {
      //this handles issue when isomorphic-style-loader addes helper functions to css-module
      return;
    }

    if (mixinValue === undefined) {
      return;
    }

    if (originalValue === undefined) {
      //mixin key is new to original theme - take it as is
      result[key] = mixinValue;
      return;
    }

    if (typeof mixinValue === 'object' && typeof originalValue === 'object') {
      //exactly nested theme object - go recursive
      result[key] = merge(originalValue, mixinValue);
      return;
    }

    if (typeof mixinValue === 'object' || typeof originalValue === 'object') {
      //can't merge a non-object with an object
      throw new Error("You are merging non-object with object, original: " + originalValue + ", mixin: " + mixinValue + ", key: " + key);
    } //finally we can merge


    result[key] = originalValue.split(' ').concat(mixinValue.split(' ')).filter(function (item, pos, self) {
      return self.indexOf(item) === pos && item !== '';
    }).join(' ');
  });
  return result;
}

function removeNamespace(key, themeNamespace) {
  var capitalized = key.substr(themeNamespace.length);
  return capitalized.slice(0, 1).toLowerCase() + capitalized.slice(1);
}

function defaultMapThemrProps(ownProps, theme) {
  var composeTheme = ownProps.composeTheme,
      //eslint-disable-line no-unused-vars
  innerRef = ownProps.innerRef,
      themeNamespace = ownProps.themeNamespace,
      //eslint-disable-line no-unused-vars
  mapThemrProps = ownProps.mapThemrProps,
      //eslint-disable-line no-unused-vars
  rest = tslib_1.__rest(ownProps, ["composeTheme", "innerRef", "themeNamespace", "mapThemrProps"]);

  return tslib_1.__assign({}, rest, {
    ref: innerRef,
    theme: theme
  });
}